import React, { useEffect, useState } from "react";
import useCountUp from "../lib/useCountUp.js";

/** Total Posture Steps ring — layered glow, breath halo, dotted markers, live count-up. */
export default function StepRing({
  active = 0,
  passive = 0,
  goal = 8000,
  size = 230,
  stroke = 14,
}) {
  const total = active + passive;
  const animTotal = useCountUp(total, { duration: 1600 });
  const pct = Math.min(1, total / Math.max(1, goal));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const activePct = Math.min(1, active / Math.max(1, goal));
  const passivePct = Math.min(1, (active + passive) / Math.max(1, goal));
  const hit = total >= goal && goal > 0;

  // Trigger pop animation when crossing goal
  const [pop, setPop] = useState(false);
  useEffect(() => {
    if (hit) {
      setPop(true);
      const t = setTimeout(() => setPop(false), 1300);
      return () => clearTimeout(t);
    }
  }, [hit]);

  // 12 tick marks
  const ticks = Array.from({ length: 12 }, (_, i) => i);

  return (
    <div className={`relative inline-flex items-center justify-center ${pop ? "ps-ring-pop" : ""}`} data-testid="step-ring">
      {/* outer breathing halo */}
      <div
        className="absolute rounded-full ps-pulse"
        style={{
          width: size + 30, height: size + 30,
          background: "radial-gradient(closest-side, rgba(74,222,128,0.18), transparent 70%)",
          filter: "blur(8px)",
        }}
      />
      <svg width={size} height={size} className="-rotate-90 relative">
        <defs>
          <linearGradient id="ring-active" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#a7f3d0" />
            <stop offset="50%" stopColor="#4ade80" />
            <stop offset="100%" stopColor="#22d3ee" />
          </linearGradient>
          <linearGradient id="ring-passive" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="100%" stopColor="#a78bfa" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#0e1f2a" strokeWidth={stroke} fill="none" />
        {/* tick marks */}
        {ticks.map((i) => {
          const a = (i / 12) * Math.PI * 2;
          const x1 = size / 2 + Math.cos(a) * (r + stroke / 2 + 6);
          const y1 = size / 2 + Math.sin(a) * (r + stroke / 2 + 6);
          const x2 = size / 2 + Math.cos(a) * (r + stroke / 2 + 11);
          const y2 = size / 2 + Math.sin(a) * (r + stroke / 2 + 11);
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#25506B" strokeWidth="1.5" strokeLinecap="round" />;
        })}
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke="url(#ring-passive)" strokeWidth={stroke} fill="none"
          strokeDasharray={c} strokeDashoffset={c - passivePct * c}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1.4s cubic-bezier(.2,.7,.2,1)" }}
        />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke="url(#ring-active)" strokeWidth={stroke} fill="none"
          strokeDasharray={c} strokeDashoffset={c - activePct * c}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1.4s cubic-bezier(.2,.7,.2,1)", filter: "drop-shadow(0 0 8px rgba(74,222,128,0.5))" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold ps-serif ps-italic px-4 text-center">Total Posture Points</div>
        <div
          className="font-extrabold text-white tracking-tight ps-heading mt-2 ps-tabular ps-count leading-none text-center"
          data-testid="total-posture-steps"
          style={{
            fontSize: (() => {
              const len = total.toLocaleString().length;
              if (len <= 4) return "2.4rem";
              if (len === 5) return "2rem";
              if (len === 6) return "1.7rem";
              if (len === 7) return "1.45rem";
              return "1.2rem";
            })(),
            maxWidth: size - stroke * 2 - 24,
          }}
        >
          {animTotal.toLocaleString()}
        </div>
      </div>
    </div>
  );
}
